<template>
  <!-- Error Message -->
  <PageError v-if="error"
   :error="error"
   :errorCode="errorCode"
  />

  <div v-else-if="canViewProbe || user?.is_staff" class="p-6 dark:text-white">
    <!-- Header Section -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
        Probes - {{ filteredProbes.length }}
        <span v-if="isFiltered" class="text-sm text-gray-500">(filtered)</span>
      </h1>
      <div class="flex gap-4">
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
        <button v-if="canAddProbe || user?.is_staff"
          id="add-probe-btn"
          @click="goToAddProbe"
          class="btn bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Probe
        </button>
        <button
          @click="showExportDialog = true"
          class="btn bg-gray-800 text-white hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          Download CSV
        </button>
      </div>
    </div>

    <!-- Organization Tabs -->
    <OrganizationTabs
      v-if="user?.is_staff || isServiceProviderUser"
      :organizations="tenant.organizations.value"
      :selected-org-id="tenant.currentOrganizationId.value"
      :loading="tenant.isLoading.value"
      :show-tabs="true"
      loading-text="Loading Probes..."
      @select="handleOrgSelect"
    />

    <!-- Search and Filter Section -->
    <div
      class="flex flex-col md:flex-row md:items-center md:justify-between mb-6"
    >
      <!-- Search Bar -->
      <div class="mb-4 md:mb-0 md:flex-1 md:mr-4">
        <SearchBar
          v-model="searchQuery"
          placeholderText="Search probes by name..."
        />
      </div>


      <!-- Filters Button (for mobile screens) -->
      <div class="mb-4 md:hidden">
        <button
          @click="showFilters = !showFilters"
          class="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          {{ showFilters ? "Hide Filters" : "Show Filters" }}
        </button>
      </div>
      
      <!-- Filters Section -->
      <div
        :class="[
          'md:flex md:items-center relative z-50 ',
          showFilters ? 'block' : 'hidden md:block',
        ]"
      >
        <FiltersSection
          :filters="dynamicFilters"
          v-model:selectedFilters="selectedFilters"
          :showFilters="showFilters"
          @update:showFilters="showFilters = $event"
          @applyFilters="applyFilters"
        />
      </div>

      <!-- Bulk Action Section -->
      <div class="flex items-center mt-4 md:mt-0 space-x-2 ml-2">
        <GlassSelect
          v-model="bulkAction"
          :options="canDeleteProbe || user?.is_staff ? [{ value: 'delete', label: 'Delete selected items' }] : []"
          placeholder="Bulk Actions"
          :valueKey="'value'"
          :labelKey="'label'"
        />
        <button
          @click="applyBulkAction"
          :disabled="!bulkAction || selectedProbes.length === 0"
          :class="[
            'btn focus:outline-none focus:ring-2 focus:ring-blue-500',
            bulkAction && selectedProbes.length
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          ]"
        >
          Apply
        </button>
      </div>
    </div>

    <!-- Probes Table -->
    <GenericTable
      id="probes-table"
      ref="table"
      :columns="columns"
      :items="paginatedProbes"
      itemKey="id"
      :selectable="true"
      @update:selectedItems="updateSelectedProbes"
      @rowClick="goToProbeDetail"
    >
      <!-- Custom cell templates -->
      <template #is_approved="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #is_installed="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #in_maintenance="{ value }">
        {{ value ? "Yes" : "No" }}
      </template>
      <template #created_date="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </GenericTable>

    <!-- Pagination -->
    <div
      class="flex flex-col md:flex-row items-center justify-between mt-8 space-y-4 md:space-y-0"
    >
      <div class="flex items-center space-x-2">
        <button
          @click="changePage(-1)"
          :disabled="currentPage === 1"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Previous
        </button>
        <button
          @click="changePage(1)"
          :disabled="currentPage === totalPages"
          class="px-4 py-2 rounded-md bg-gray-800 text-gray-100 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
        >
          Next
        </button>
      </div>
      <div class="text-gray-700 dark:text-white">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <div>
        <GlassSelect
          v-model="itemsPerPage"
          :options="[{ value: 10, label: '10 per page' }, { value: 20, label: '20 per page' }, { value: 50, label: '50 per page' }, { value: 100, label: '100 per page' }, { value: -1, label: 'All' }]"
          :valueKey="'value'"
          :labelKey="'label'"
          placeholder="Items per page"
          @change="updatePagination"
        />
      </div>
    </div>
    <ProbeCsvExportDialog
      :is-open="showExportDialog"
      :data="filteredProbes"
      @close="showExportDialog = false"
    />

    <DeleteModal
      v-if="deletingItem"
      :deleting-item="deletingItem"
      :componet-objects="componetObjects"
      :is-deleting="isDeleting"
      @delete="confirmDelete"
      @close="deletingItem = false"
    />
  </div>
</template>

<script>
import { getAllProbes, deleteProbe } from "@/services/probeService";
import SearchBar from "@/components/SearchBar.vue";
import GenericTable from "@/components/GenericTable.vue";
import ProbeCsvExportDialog from "@/components/ProbeCsvExportDialog.vue";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { getAllSubnets } from "@/services/subnetService";
import { getAllVlans } from "@/services/vlanService";
import { getAllBlackboxes } from "@/services/blackboxService";
import FiltersSection from "@/components/FiltersSection.vue";
import DeleteModal from "@/components/DeleteModal.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "ProbeView",
  components: {
    SearchBar,
    GenericTable,
    ProbeCsvExportDialog,
    PageError,
    FiltersSection,
    DeleteModal,
    OrganizationTabs,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      probes: [],
      searchQuery: "",
      filteredProbes: [],
      currentPage: 1,
      itemsPerPage: 10,
      selectedProbes: [],
      bulkAction: "",
      deletingItem: false,
      showExportDialog: false,
      vlans:[],
      subnets:[],
      blackboxes:[],
      columns: [
        { key: "name", label: "Name", sortable: true, width: "25%" },
        { key: "device", label: "Device", sortable: true, width: "15%" },
        { key: "is_approved", label: "Approved", sortable: true, width: "10%" },
        {
          key: "is_installed",
          label: "Installed",
          sortable: true,
          width: "10%",
        },
        {
          key: "in_maintenance",
          label: "In Maintenance",
          sortable: true,
          width: "15%",
        },
        {
          key: "created_date",
          label: "Created Date",
          sortable: true,
          width: "10%",
        },
      ],
      dynamicFilters: [],
      canAddProbe: true,
      canDeleteProbe: true,
      canViewProbe: true,
      error: "",
      errorCode: "",
      errorMessages: {
        400 : "Bad Request .The server could not understand the request.",
        401 : "Unauthorized access. Please check your authentication token or log in again.",
        403 : "You do not have permission to access this resource. Contact a staff member to assign you the permissions to access this page.",
        404 : "Page Not Found.",
        500 : "Internal Server Error - Something went wrong on the server.",
        422 : "Malformed request. Failed to load Probes!"
      },
      selectedFilters: {
        organizationFilter: "",
        maintenanceFilter: "",
        installedFilter: "",
        approvedFilter: "",
      },
      showFilters: false,
      organizations: [],
      isDeleting: false,
      probeTutorialSteps: [
        {
          element: '#searchbar',
          action: 'display',
          message: 'Search probes by name or use filters to narrow results.'
        },
        {
          element: '#add-probe-btn',
          action: 'display',
          message: 'Click here to create a new probe.'
        },
        {
          element: '#probes-table',
          action: 'display',
          message: 'Click any row to view or edit probe details.'
        },
      ],
    };
  },
  computed: {
    user(){
      return useStore.getUser();
    },
    isServiceProviderUser() {
      return useStore.isServiceProviderUser();
    },
    isFiltered() {
      return (
        this.searchQuery !== "" ||
        this.selectedFilters.maintenanceFilter !== "" ||
        this.selectedFilters.organizationFilter !== "" ||
        this.selectedFilters.installedFilter !== "" ||
        this.selectedFilters.approvedFilter !== ""
      );
    },
    totalPages() {
      return this.itemsPerPage === -1
        ? 1
        : Math.ceil(this.filteredProbes.length / this.itemsPerPage);
    },
    paginatedProbes() {
      if (this.itemsPerPage === -1) {
        return this.filteredProbes;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProbes.slice(start, end);
    },
    componetObjects() {
      if(this.selectedProbes){
      return this.selectedProbes.map(probe => ({
        values:  [
          { name: "Probe", value: probe.name},
          { name: "Subnets", value: probe.related_objects.subnet_count },
          { name: "VLANs", value: probe.related_objects.vlan_count },
          { name: "Endpoint Availabilities", value: probe.related_objects.blackbox_count},
          { name: "Probe SNMPs", value: probe.related_objects.snmp_count},
          { name: "Network Devices", value: probe.related_objects.network_device_count }
          ]
        }));
      }
    }
  },
  watch: {
    searchQuery() {
      this.applyFilters();
    }, 
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
        }
      },
      immediate: true
    },
  },
 async mounted() {
    this.checkPermissions()
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
        await this.tenant.syncUrlParam(); 
        await this.fetchProbes(this.tenant.currentSchemaName.value);
    } else {
        await this.tenant.loadInitialData(this.fetchProbes);
    }
    this.initializeFilters();
  },
  methods: {
    async startTutorial() {
      await this._start(this.probeTutorialSteps);
    },
    async checkPermissions(){
      if(this.user){
        this.canAddProbe = await useStore.hasPermission("Can add probe");
        this.canDeleteProbe = await useStore.hasPermission("Can delete probe");
        this.canViewProbe = await useStore.hasPermission("Can view probe");
      }
    },
    async handleOrgSelect(orgId) {
      try {
        this.error = "";
        await this.tenant.switchOrganization(orgId, this.fetchProbes);
      } catch (error) {
        this.error = "Failed to switch organization";
        console.error('Organization select error:', error);
      }
    },
    async fetchProbes(schemaName = null) {
      try {
        const probes = await getAllProbes(schemaName);
        this.probes = [...probes];
        this.applyFilters();
      } catch (error) {
        if (error.response) {
          this.error = `${this.errorMessages[error.response.status]}`;
          this.errorCode = `${error.response.status}`;
        } else{
          this.error = `Unknown Error`
        }
      }
    },
    goToProbeDetail(probe) {
      this.$router.push({
        name: "ProbeDetails",
        params: { id: probe.id },
        query: this.tenant.getDetailPageParam()
      });
    },
    goToAddProbe() {
      this.$router.push({
        name: "AddProbe",
        query: this.tenant.addSchemaParam()
       });
    },
    applyFilters() {
      this.filteredProbes = this.probes.filter((probe) =>
        probe.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );

      this.filteredProbes = this.probes.filter((probe) => {
        const nameMatch = probe.name
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase());

        const maintenanceMatch =
          this.selectedFilters.maintenanceFilter === "" ||
          probe.in_maintenance.toString() ===
            this.selectedFilters.maintenanceFilter;

        const approvedMatch =
          this.selectedFilters.approvedFilter === "" ||
          probe.is_approved.toString() ===
            this.selectedFilters.approvedFilter;
            
        const installedMatch =
          this.selectedFilters.installedFilter === "" ||
          probe.is_installed.toString() ===
            this.selectedFilters.installedFilter;    

        const organizationMatch =
          this.selectedFilters.organizationFilter === "" ||
          probe.org.toString() === this.selectedFilters.organizationFilter;

        return nameMatch && maintenanceMatch && installedMatch && approvedMatch && organizationMatch;
      });
      this.currentPage = 1;
    },
    updateSelectedProbes(selectedItems) {
      this.selectedProbes = selectedItems.filter((item => !item.marked_for_deletion));
    },
    async applyBulkAction() {
      if (this.bulkAction === "delete" ) {
        this.deletingItem = true;
      }
      this.bulkAction = "";
    },
    async confirmDelete() {
      this.isDeleting = true;
      await this.deleteSelectedProbes();
      this.deletingItem = false; 
      this.bulkAction = "";
    },
    async deleteSelectedProbes() {
      if (this.selectedProbes.length === 0) {
        alert("No probes selected for deletion");
        return;
      }
      const schemaName = this.tenant.currentSchemaName.value;
      if (!schemaName) {
        this.error = "Malformed request. Failed to delete selected probes!";
        this.errorCode = "422!";
        return;
      }

      try {
        const deletePromises = this.selectedProbes.map((probe) =>
          deleteProbe(probe.id, schemaName)
        );
        await Promise.all(deletePromises);
        this.fetchProbes(schemaName);
        this.$refs.table.clearSelection();
        this.applyFilters();
        this.selectedProbes = [];
      } catch (error) {
        console.error("Error during bulk delete:", error);
        alert("An error occurred while deleting probes. Please try again.");
      }  finally {
        this.isDeleting = false;
      }
    },
    formatSnmpTargets(value) {
      if (Array.isArray(value)) {
        return value.join(", ");
      } else if (value === null || value === undefined) {
        return "N/A";
      } else {
        return String(value);
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },

    changePage(delta) {
      this.currentPage += delta;
    },
    updatePagination() {
      this.currentPage = 1;
    },
    initializeFilters() {
      this.dynamicFilters = [
        {
          name: "maintenanceFilter",
          label: "Probe in Maintenance",
          options: [
            { value: "", label: "All" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ],
        },
        {
          name: "approvedFilter",
          label: "Probe Approved",
          options: [
            { value: "", label: "All" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ],
        },
        {
          name: "installedFilter",
          label: "Probe Installed",
          options: [
            { value: "", label: "All" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ],
        },
        {
          name: "organizationFilter",
          label: "Organisation",
          options: [{ value: "", label: "All" }].concat(
            this.tenant.organizations.value.map((org) => ({
              value: String(org.id),
              label: org.name,
            }))
          ),
        },
      ];
    },
  },
};
</script>
